import { useCallback, useEffect, useState } from 'react';
import { bizRecordsAPI } from '../services/bizApi';

const PAGE_SIZE = 10;

// Slugs whose Workspace section is wired to a real, org-scoped API instead of
// static sample data. Grows as each Phase 2 module ships.
export const LIVE_SLUGS = new Set(['contacts', 'receipts', 'transactions', 'invoices']);

export function useLiveWorkspaceModule(slug) {
  const isLive = LIVE_SLUGS.has(slug);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [errorMessage, setErrorMessage] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const load = useCallback(async () => {
    if (!isLive) return;
    setStatus('loading');
    setErrorMessage(null);
    try {
      if (slug === 'contacts') {
        const { data } = await bizRecordsAPI.listContacts({ q: search || undefined, page, page_size: PAGE_SIZE });
        setItems(data.contacts);
        setTotal(data.total);
      } else if (slug === 'receipts') {
        const { data } = await bizRecordsAPI.listReceipts({ q: search || undefined, category: category || undefined, page, page_size: PAGE_SIZE });
        setItems(data.receipts);
        setTotal(data.total);
      } else if (slug === 'transactions') {
        const { data } = await bizRecordsAPI.listTransactions({ q: search || undefined, category: category || undefined, page, page_size: PAGE_SIZE });
        setItems(data.transactions);
        setTotal(data.total);
      } else if (slug === 'invoices') {
        const { data } = await bizRecordsAPI.listWorkspaceInvoices({ q: search || undefined, status: category || undefined, page, page_size: PAGE_SIZE });
        setItems(data.invoices);
        setTotal(data.total);
      }
      setStatus('ready');
    } catch (e) {
      if (e?.response?.status === 403) setPermissionDenied(true);
      setErrorMessage(e?.response?.data?.detail || 'Something went wrong loading this data.');
      setStatus('error');
    }
  }, [isLive, slug, search, category, page]);

  useEffect(() => { load(); }, [load]);

  const create = useCallback(async (fields) => {
    if (slug === 'contacts') return bizRecordsAPI.createContact(fields);
    if (slug === 'transactions') return bizRecordsAPI.createTransaction(fields);
    if (slug === 'invoices') return bizRecordsAPI.createWorkspaceInvoice(fields);
    if (slug === 'receipts') {
      const formData = new FormData();
      Object.entries(fields).forEach(([k, v]) => { if (v !== null && v !== undefined) formData.append(k, v); });
      return bizRecordsAPI.createReceipt(formData);
    }
    throw new Error('Not implemented');
  }, [slug]);

  const update = useCallback(async (id, fields) => {
    if (slug === 'contacts') return bizRecordsAPI.updateContact(id, fields);
    if (slug === 'receipts') return bizRecordsAPI.updateReceipt(id, fields);
    if (slug === 'transactions') return bizRecordsAPI.updateTransaction(id, fields);
    if (slug === 'invoices') return bizRecordsAPI.updateWorkspaceInvoice(id, fields);
    throw new Error('Not implemented');
  }, [slug]);

  const remove = useCallback(async (id) => {
    if (slug === 'contacts') return bizRecordsAPI.deleteContact(id);
    if (slug === 'receipts') return bizRecordsAPI.deleteReceipt(id);
    if (slug === 'transactions') return bizRecordsAPI.deleteTransaction(id);
    if (slug === 'invoices') return bizRecordsAPI.deleteWorkspaceInvoice(id);
    throw new Error('Not implemented');
  }, [slug]);

  return {
    isLive, items, total, page, setPage, pageSize: PAGE_SIZE,
    search, setSearch, category, setCategory, status, errorMessage, permissionDenied,
    reload: load, create, update, remove,
  };
}
